import { Component, ElementRef, OnDestroy, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import * as bowser from 'bowser';
import { Subject } from 'rxjs';

import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { ToastService } from '@app/core/services/toast.service';

import { FormHelper } from '@app/ui-forms/classes/form-helper';

import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';

@Component({
	selector: 'cng-callback-form',
	templateUrl: './callback-form.component.html',
	styleUrls: ['./callback-form.component.less'],
})
export class ConfigureCallbackFormComponent implements OnInit, OnDestroy {
	@Input() location: string = '';
	@Output() callback_created: EventEmitter<any> = new EventEmitter<any>();

	customer: any = this.configure_service.configure_data.meta.customer;
	callback: any = this.configure_service.configure_data.meta.callback;
	callback_form: FormGroup;
	callback_edit: boolean = false;
	autosize_notes: boolean = !bowser.ios;

	day_of_week_options: Array<any> = [
		{ value: '' },
		{ value: 'any', name: 'Any' },
		{ value: 'weekdays', name: 'Weekdays' },
		{ value: 'weekends', name: 'Weekends' },
		{ value: 'monday', name: 'Monday' },
		{ value: 'tuesday', name: 'Tuesday' },
		{ value: 'wednesday', name: 'Wednesday' },
		{ value: 'thursday', name: 'Thursday' },
		{ value: 'friday', name: 'Friday' },
		{ value: 'saturday', name: 'Saturday' },
		{ value: 'sunday', name: 'Sunday' },
	];
	time_of_day_options: Array<any> = [
		{ value: '' },
		{ value: 'any', name: 'Any' },
		{ value: 'morning', name: 'Morning' },
		{ value: 'afternoon', name: 'Afternoon' },
		{ value: 'evening', name: 'Evening' },
	];

	loading: boolean = false;
	tracking_log: string[] = [];
	destroyed$: Subject<boolean> = new Subject<boolean>();

	constructor(
		private configure_service: ConfigureService,
		private toast_service: ToastService,
		private element_ref: ElementRef,
		private tracking_service: TrackingService
	) {}

	ngOnInit(): void {
		this.buildForm();
		this.populateForm();

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.callback = configure_data.meta.callback;
			this.customer = configure_data.meta.customer;
			this.populateForm();
		});
	}

	buildForm(): void {
		this.callback_form = new FormGroup({
			first_name: new FormControl('', Validators.required),
			last_name: new FormControl('', Validators.required),
			email: new FormControl('', [Validators.required, Validators.email]),
			tel_mob: new FormControl('', [Validators.required, Validators.pattern('[0-9 +]+')]),
			day_of_week: new FormControl('', Validators.required),
			time_of_day: new FormControl('', Validators.required),
			notes: new FormControl(''),
		});
	}

	populateForm(): void {
		this.callback_form.controls['first_name'].setValue(this.customer.first_name);
		this.callback_form.controls['last_name'].setValue(this.customer.last_name);
		this.callback_form.controls['email'].setValue(this.customer.email);
		this.callback_form.controls['tel_mob'].setValue(this.customer.tel_mob);

		if (this.callback !== null) {
			this.callback_form.controls['day_of_week'].setValue(this.callback.day_of_week);
			this.callback_form.controls['time_of_day'].setValue(this.callback.time_of_day);
			this.callback_form.controls['notes'].setValue(this.callback.notes);
		}
	}

	onSubmit(): void {
		this.onCallbackSubmit();
	}

	onCallbackSubmit(): void {
		if (this.callback === null || this.callback_edit) {
			this.tracking_service.trackFormSubmitAttempt(
				'callback_form',
				FormHelper.getCompletedControls(this.callback_form),
				FormHelper.getEmptyControls(this.callback_form)
			);
			if (this.callback_form.invalid) {
				FormHelper.forceValidate(this.callback_form);
				this.tracking_service.trackFormValidationFailure(
					'callback_form',
					'invalid_fields[' + FormHelper.getInvalidControls(this.callback_form).join(',') + ']'
				);
				this.toast_service.error('Form has invalid fields');
				this.loading = false;
			} else {
				this.tracking_service.trackFormValidationPass('callback_form');
				this.loading = true;

				const body: any = {
					first_name: this.callback_form.controls['first_name'].value,
					last_name: this.callback_form.controls['last_name'].value,
					email: this.callback_form.controls['email'].value.toLowerCase().replace(/ /g, ''),
					tel_mob: this.callback_form.controls['tel_mob'].value.toLowerCase().replace(/ /g, ''),
					day_of_week: this.callback_form.controls['day_of_week'].value,
					time_of_day: this.callback_form.controls['time_of_day'].value,
					notes: this.callback_form.controls['notes'].value,
				};

				this.configure_service.saveCallback(body).subscribe((success: boolean) => {
					this.loading = false;
					if (success) {
						this.callback_edit = false;
						this.toast_service.success('Callback requested');
						this.tracking_service.trackFormSubmitSuccess('callback_form');
						this.tracking_service.trackCallback();
						this.callback_created.emit();
					}
				});
			}
		} else {
			this.callback_edit = true;
			this.loading = false;
		}
	}

	onAddNotes(): void {
		this.onCallbackSubmit();
		this.callback_form.controls['notes'].reset();
		setTimeout(() => {
			this.element_ref.nativeElement.querySelectorAll('.callback textarea[name="notes"]')[0].focus();
		});
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
